import { Box, Link } from '@mui/material';

import { ALLOWED_BBCODE_TAGS } from './bbcodeTags';
import { NavLink } from 'react-router-dom';
import TagNode from '@bbob/plugin-helper/es/TagNode';
import { isTagNode } from '@bbob/plugin-helper/es';
import reactPreset from '@bbob/preset-react';
import { render as reactRender } from '@bbob/react';
import { sanitizeAndCleanHtml } from '/b2b/common/helpers/DOM';
import { validateURL } from '/b2b/common/helpers/URL';

/**
 * Parses the sx object and returns a new object with the values converted to numbers if they are valid numbers.
 * This allows us to use valid MUI shorthand and theme values in our translation strings
 * Example: "common.foobar": "[style color=primary.main mt=2]foobar[/style]"
 * @param {Object} sx - The sx object to parse
 * @returns {Object} The parsed sx object
 */
const parseSx = (sx = {}) =>
    Object.entries(sx).reduce((acc, [key, value]) => {
        try {
            const numberValue = Number(value);
            acc[key] = Number.isNaN(numberValue) ? value : numberValue;
        } catch (error) {
            acc[key] = value;
        }
        return acc;
    }, {});

const bbobPreset = reactPreset();

const linkParsePlugin = (tree, options = {}) => {
    return tree.walk(node => {
        if (!isTagNode(node)) {
            return node;
        }

        switch (node.tag) {
            case 'function': {
                const functionName = Object.keys(node.attrs)[0];
                const functionHandler = options[functionName];
                return functionHandler
                    ? TagNode.create(
                          Link,
                          {
                              component: 'button',
                              onClick: functionHandler,
                              sx: {
                                  fontSize: 'inherit',
                                  verticalAlign: 'baseline',
                              },
                          },
                          node.content
                      )
                    : node;
            }
            case 'html': {
                const htmlContent = Array.isArray(node.content) ? node.content.join('') : '';
                const sanitizedHtml = sanitizeAndCleanHtml(htmlContent || '');
                const sx = node.attrs || {};
                return sanitizedHtml
                    ? TagNode.create(Box, {
                          dangerouslySetInnerHTML: { __html: sanitizedHtml },
                          sx: parseSx(sx),
                      })
                    : node;
            }
            case 'style': {
                const sx = node.attrs || {};
                return Object.keys(sx).length
                    ? TagNode.create(Box, { component: 'span', sx: parseSx(sx) }, node.content)
                    : node;
            }
            case 'url': {
                const href = Object.keys(node.attrs)[0];
                if (validateURL(href) && href.startsWith('http')) {
                    return TagNode.create(Link, { href, target: '_blank' }, node.content);
                } else {
                    return TagNode.create(
                        NavLink,
                        {
                            component: Link,
                            to: href,
                        },
                        node.content
                    );
                }
            }
            case 'component': {
                const attrs = node.attrs || {};
                const componentName = Object.keys(attrs)[0];

                if (!componentName) {
                    return node;
                }

                const Component = options[componentName];

                if (!Component) {
                    return node;
                }

                // Extract props by removing the component name from attrs
                const { [componentName]: _, ...props } = attrs;

                // Parse props similar to parseSx to handle MUI theme values and numbers
                const parsedProps = parseSx(props);

                return TagNode.create(Component, parsedProps, node.content);
            }
            default: {
                return node;
            }
        }
    });
};

function BBCodeFormatter(translatedText, options = {}) {
    return reactRender(translatedText, [tree => linkParsePlugin(tree, options)], {
        preset: bbobPreset,
        onlyAllowTags: ALLOWED_BBCODE_TAGS,
    });
}

BBCodeFormatter.supportsOptions = true;

export default BBCodeFormatter;
